import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { store } from '../redux/store';
import { logout, logoutForServiceUnavailable, setCurrentClient } from '../redux/auth/authSlice';
import { buildApiUrl } from './urlCreator';
import { getAccessToken } from './authSession';
import { isServiceUnavailableError } from './handleAxiosError';

type RetryableRequest = InternalAxiosRequestConfig & { _retry?: boolean };

let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string) => void; reject: (error: unknown) => void }> = [];
let configured = false;

const authClient = axios.create({
    withCredentials: true,
    headers: { 'X-TCGATE-UI': '1' },
});

const publicApiOrigin = new URL(import.meta.env.VITE_TCGATE_PUBLIC_API_URL).origin;

const isPublicApiRequest = (url?: string) => {
    if (!url) return false;

    try {
        return new URL(url, window.location.origin).origin === publicApiOrigin;
    } catch {
        return false;
    }
};

const processQueue = (error: unknown, token?: string) => {
    failedQueue.forEach(({ resolve, reject }) => error || !token ? reject(error) : resolve(token));
    failedQueue = [];
};

const isAuthenticationRequest = (url?: string) =>
    !!url && [
        '/token/login',
        '/token/refreshTokenLogin',
        '/token/logout',
        '/twoFactorAuthentication/validateOTP',
    ].some((path) => url.includes(path));

const setupAxios = () => {
    if (configured) return;
    configured = true;

    axios.defaults.headers.common.Accept = 'application/json';

    axios.interceptors.request.use((config) => {
        const isApiRequest = isPublicApiRequest(config.url);
        config.withCredentials = isApiRequest;

        if (!isApiRequest) {
            delete config.headers.Authorization;
            delete config.headers['X-TCGATE-UI'];
            return config;
        }

        config.headers['X-TCGATE-UI'] = '1';
        const accessToken = getAccessToken();
        if (accessToken && !isAuthenticationRequest(config.url)) {
            config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return config;
    });

    axios.interceptors.response.use(
        (response) => response,
        async (error: AxiosError) => {
            const originalRequest = error.config as RetryableRequest | undefined;
            if (!originalRequest || !isPublicApiRequest(originalRequest.url)) {
                return Promise.reject(error);
            }

            if (isServiceUnavailableError(error)) {
                if (!isAuthenticationRequest(originalRequest.url) && store.getState().auth.currentClient) {
                    store.dispatch(logoutForServiceUnavailable());
                }
                return Promise.reject(error);
            }

            if (error.response?.status !== 401 || originalRequest._retry || isAuthenticationRequest(originalRequest.url)) {
                return Promise.reject(error);
            }

            if (isRefreshing) {
                return new Promise<string>((resolve, reject) => failedQueue.push({ resolve, reject }))
                    .then((token) => {
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                        return axios(originalRequest);
                    });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const response = await authClient.post(buildApiUrl('token/refreshTokenLogin'));
                const token = response.data?.accessToken as string | undefined;
                if (!token) throw new Error('The refreshed session did not include an access token.');

                store.dispatch(setCurrentClient(response.data));
                processQueue(null, token);
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return axios(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError);
                store.dispatch(isServiceUnavailableError(refreshError) ? logoutForServiceUnavailable() : logout());
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        },
    );
};

export { authClient };
export default setupAxios;
